import * as jQuery from "jquery";

export type Bounds = {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
};

export type CopyDOMResult<T> = {
  element: T;
  html: string;
  bounds: Bounds;
};

export interface PopupOptions {
  toolbar?: boolean;
  location?: boolean;
  directories?: boolean;
  status?: boolean;
  menubar?: boolean;
  scrollbars?: boolean;
  resizeable?: boolean;
  width?: number | string;
  height?: number | string;
  top?: number | string;
  left?: number | string;
}

export const defaultPopupOptions: PopupOptions = {
  toolbar: false,
  location: false,
  directories: false,
  status: false,
  menubar: false,
  scrollbars: true,
  resizeable: true,
  width: undefined,
  height: undefined,
  top: undefined,
  left: undefined,
};

export function escapeHtml(unsafe: string) {
  const text = document.createTextNode(unsafe);
  const div = document.createElement("DIV");
  div.appendChild(text);
  return div.innerHTML;
}

export function copyElementStyles(src: HTMLElement, dest: HTMLElement) {
  if (!src || !dest) return;

  const computedStyle = getComputedStyle(src);

  for (let i = 0; i < computedStyle.length; i++) {
    const prop = computedStyle[i];
    const value = computedStyle.getPropertyValue(prop);
    dest.style.setProperty(prop, value);
  }

  for (let i = 0; i < src.children.length; i += 1) {
    copyElementStyles(src.children[i] as HTMLElement, dest.children[i] as HTMLElement);
  }
}

export function cloneElement<T extends HTMLElement>(element: T): T {
  const output = element.cloneNode(true) as T;
  copyElementStyles(element, output);
  return output;
}

export function centerPopup(w: number, h: number): Bounds {
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screen["left"];
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screen["top"];

  const width = window.innerWidth ? window.innerWidth :
    (document.documentElement.clientWidth ? document.documentElement.clientWidth : window.screen.width);
  const height = window.innerHeight ? window.innerHeight :
    (document.documentElement.clientHeight ? document.documentElement.clientHeight : window.screen.height);

  const left = ((width / 2) - (w / 2)) + dualScreenLeft;
  const top = ((height / 2) - (h / 2)) + dualScreenTop;

  return {width: w, height: h, left, top} as Bounds;
}

export function copyDOM<T extends HTMLElement>(
  sourceElement: T,
  targetContainer: HTMLElement,
  removeWhenDone: boolean = false,
  centerObject: boolean = false,
): CopyDOMResult<T> {
  const element = cloneElement(sourceElement) as T;

  targetContainer.appendChild(element as HTMLElement);

  const rect: ClientRect = element.getBoundingClientRect();

  const bounds = centerObject ?
    centerPopup(rect.width + 48, rect.height + 96) :
    {width: rect.width, height: rect.height, left: rect.left, top: rect.top} as Bounds;

  const html: string = element.outerHTML;

  if (removeWhenDone) {
    targetContainer.removeChild(element as HTMLElement);
  }

  return {html, element, bounds};
}

function translatePopupBooleanOption(options: PopupOptions | any, key: string) {
  if (options[key] === undefined) {
    return defaultPopupOptions[key];
  }

  if (/^(?:width|height|top|left)$/.test(key)) {
    return options[key] ? `${key}=${options[key]}` : undefined;
  }

  return options[key] ? `${key}=yes` : `${key}=no`;
}

export function stringifyPopupOptions(options: PopupOptions | any): string {
  return Object.keys(defaultPopupOptions)
    .map(it => translatePopupBooleanOption(options, it))
    .filter(it => it)
    .join(", ");
}

const PRINT_PREVIEW_WARNING_MODAL_TEMPLATE = `
<div class="modal fade" tabindex="-1" role="dialog" id="popupWarningModalTemplate" aria-hidden="true">
  <div class="modal-dialog" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Print Preview Error</h5>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        Your browser is blocking popups from this site.  Please enable popups to open the print preview.
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary printPreviewRetryButton">Retry</button>
        <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
      </div>
    </div>
  </div>
</div>
`;

function insertPrintPreviewWarningModal() {
  const exists = jQuery("#popupWarningModalTemplate").length > 0;
  if (exists) return;
  jQuery("body").append(PRINT_PREVIEW_WARNING_MODAL_TEMPLATE);
}

function removePrintPreviewWarningModal() {
  const item = jQuery("#popupWarningModalTemplate");
  if (item) item.remove();
}

export async function safeOpenWindow(
  url?: string,
  target?: string,
  features?: string,
  replace?: boolean,
): Promise<Window> {
  let win = window.open(url, target, features, replace);
  if (win) return win;

  insertPrintPreviewWarningModal(); // append modal to body

  const modalObj = (jQuery("#popupWarningModalTemplate") as any) as {modal: Function, on: Function};
  const modalObjRetryButton = (jQuery(".printPreviewRetryButton", modalObj) as any) as {on: Function};
  modalObj.modal({show: false}); // don't show modal until events are applied

  await new Promise((resolve) => {
    // on click: try to open window, then hide the modal if successful
    modalObjRetryButton.on(
      "click",
      () => {
        win = window.open(url, target, features, replace);
        if (win) {
          modalObj.modal("hide");
        }
      },
    );

    // on close: return the opened window (if any)
    modalObj.on(
      "hidden.bs.modal",
      () => {
        resolve(win);
      },
    );

    // show the modal
    modalObj.modal("show");
  });

  removePrintPreviewWarningModal(); // remove modal from body

  return win;
}

export async function printPreview(
  sourceElement: HTMLElement,
  title: string = "Print",
  showPrintDialogImmediately: boolean = true,
  options: PopupOptions = defaultPopupOptions,
): Promise<boolean> {
  const initialPopupBounds = centerPopup(440, 240);
  const popupOptions = stringifyPopupOptions({...options, ...initialPopupBounds});
  const win = await safeOpenWindow("", "", popupOptions);

  if (!win) throw new Error("Popup blocker prevented print preview.");

  win.document.head.innerHTML = `<title>${escapeHtml(title)}</title>`;
  win.document.body.innerHTML = `<h1 style="text-align: center;">Preparing print preview</h1>`;

  const htmlElement = document.querySelector("html") as HTMLHtmlElement;
  const bodyElement = document.querySelector("body") as HTMLBodyElement;
  const originalClassName = htmlElement.className;
  htmlElement.className = `${htmlElement.className} print-preview-assist`;

  const printPreviewContainer = document.createElement("DIV");
  printPreviewContainer.className = "print-preview-container";
  bodyElement.appendChild(printPreviewContainer);

  const {html, bounds} = copyDOM(sourceElement, printPreviewContainer, true, true);

  bodyElement.removeChild(printPreviewContainer);

  htmlElement.className = originalClassName;

  win.document.body.innerHTML = html;
  win.moveTo(bounds.left, bounds.top);
  win.resizeTo(bounds.width, bounds.height);

  if (showPrintDialogImmediately) {
    win.print();
  }

  return true;
}
